import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, Mail, MessageSquare, CreditCard, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NotificationSettings = ({ user }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/settings/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (key, value) => {
    const updatedSettings = { ...settings, [key]: value };
    setSettings(updatedSettings);
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/settings/notifications`,
        { [key]: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Einstellung gespeichert');
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Fehler beim Speichern');
      // Revert on error
      setSettings({ ...settings, [key]: !value });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-3xl shadow-xl p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="space-y-4">
            <div className="h-16 bg-gray-100 rounded"></div>
            <div className="h-16 bg-gray-100 rounded"></div>
            <div className="h-16 bg-gray-100 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-3xl shadow-xl p-8">
      <div className="flex items-center gap-3 mb-6">
        <Bell className="w-6 h-6 text-[#2B2142]" />
        <h2 className="text-2xl font-bold text-gray-900">Benachrichtigungen</h2>
      </div>

      <div className="space-y-6">
        {/* Provider-specific settings */}
        {user?.role === 'provider' && (
          <>
            <div className="flex items-center justify-between py-4">
              <div className="flex items-start gap-3 flex-1">
                <MessageSquare className="w-5 h-5 text-gray-600 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900">Neue Anfragen</h3>
                  <p className="text-sm text-gray-600">
                    Benachrichtigung bei neuen Kundenanfragen
                  </p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings?.notify_new_requests ?? true}
                  onChange={(e) => handleToggle('notify_new_requests', e.target.checked)}
                  className="sr-only peer"
                  disabled={saving || !settings?.email_notifications_enabled}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#2B2142]/30 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2B2142] disabled:opacity-50"></div>
              </label>
            </div>

            <div className="flex items-center justify-between py-4">
              <div className="flex items-start gap-3 flex-1">
                <AlertCircle className="w-5 h-5 text-gray-600 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900">Neue Bewertungen</h3>
                  <p className="text-sm text-gray-600">
                    Benachrichtigung bei neuen Kundenbewertungen
                  </p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings?.notify_new_reviews ?? true}
                  onChange={(e) => handleToggle('notify_new_reviews', e.target.checked)}
                  className="sr-only peer"
                  disabled={saving || !settings?.email_notifications_enabled}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#2B2142]/30 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2B2142] disabled:opacity-50"></div>
              </label>
            </div>

            <div className="flex items-center justify-between py-4 bg-yellow-50 px-4 rounded-lg">
              <div className="flex items-start gap-3 flex-1">
                <CreditCard className="w-5 h-5 text-yellow-600 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900">Konto & Abrechnung</h3>
                  <p className="text-sm text-gray-600">
                    Wichtige Informationen zu deinem Abo (nicht abwählbar)
                  </p>
                </div>
              </div>
              <div className="w-11 h-6 bg-yellow-600 rounded-full relative">
                <div className="absolute top-[2px] right-[2px] bg-white border-white border rounded-full h-5 w-5"></div>
              </div>
            </div>
          </>
        )}

        {/* Customer-specific settings */}
        {user?.role === 'customer' && (
          <>
            <div className="flex items-center justify-between py-4">
              <div className="flex items-start gap-3 flex-1">
                <Mail className="w-5 h-5 text-gray-600 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900">Newsletter & Event-Tipps</h3>
                  <p className="text-sm text-gray-600">
                    Inspirierende Ideen und Tipps für dein nächstes Event
                  </p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings?.notify_newsletter ?? false}
                  onChange={(e) => handleToggle('notify_newsletter', e.target.checked)}
                  className="sr-only peer"
                  disabled={saving}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#2B2142]/30 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2B2142] disabled:opacity-50"></div>
              </label>
            </div>
          </>
        )}
      </div>

      <div className="mt-6 p-4 bg-[#FFF0D1]/30 border border-[#2B2142]/20 rounded-xl">
        <p className="text-sm text-[#2B2142]">
          💡 <strong>Hinweis:</strong> Du kannst hier festlegen, welche Benachrichtigungen du erhältst. 
          Details findest du in unserer <a href="/datenschutz" className="underline hover:text-[#2B2142]">Datenschutzerklärung</a>.
        </p>
      </div>
    </div>
  );
};

export default NotificationSettings;
